/**
 * 便當合一 P2（Owner 2026-10-14）：「變體選項組」骨架。
 *
 * 代碼只負責：
 *  - 讀 Admin 設定（邊個選項組係變體組、每個選項嘅編號前綴、三個顯示範本、預設選項）
 *  - 用範本拼字（製作單／收據／報表各自一個範本）
 *  - 平台貨品編號（例如 C12）對返「產品＋選項」
 * 所有內容（飯底名、前綴、範本、預設、加減價、平台分開／合一）全部由 Admin 設定，唔寫死。
 */
export const MFK_PRODUCT_VARIANT_SCHEMA='MFK_PRODUCT_VARIANT_V1' as const;

/** 範本可用欄位。{飯底名} 同 {選項名} 係同一樣嘢（方便唔係飯底嘅變體組用）。 */
export const PRODUCT_VARIANT_TEMPLATE_TOKENS=Object.freeze([
  {token:'{前綴}',label:'選項編號前綴（例如 C）'},
  {token:'{產品編號}',label:'產品編號（例如 12）'},
  {token:'{編號}',label:'前綴＋產品編號（例如 C12）'},
  {token:'{產品名}',label:'產品名（例如 鹽酥雞）'},
  {token:'{短名}',label:'產品短名；冇設定就用產品名'},
  {token:'{飯底名}',label:'所揀選項名（例如 咖喱）'},
  {token:'{選項名}',label:'同 {飯底名}'},
  {token:'{組名}',label:'選項組名（例如 飯底）'},
] as const);

export interface ProductVariantTemplatesV1{
  /** 製作單／打包單／標籤 */
  readonly kitchen:string;
  /** 收據／枱單／購物車／訂單紀錄 */
  readonly receipt:string;
  /** 報表 */
  readonly report:string;
}

export type ChannelVariantMode='SPLIT'|'MERGED';

export interface ProductVariantSetConfigV1{
  readonly enabled:boolean;
  readonly templates:ProductVariantTemplatesV1;
  /** 產品冇自己預設時用呢個（例如 肉燥）；空白 = 冇預設，要人揀 */
  readonly defaultOptionId:string;
  /** 製作單／收據嘅細項行係咪仲顯示「飯底：咖喱」（名已經有就可以關） */
  readonly showInDetail:boolean;
}

/** Admin 開變體組時嘅起始範本（Owner 例子）；Owner 可以隨時改。 */
export const DEFAULT_PRODUCT_VARIANT_TEMPLATES:ProductVariantTemplatesV1=Object.freeze({
  kitchen:'{編號} {產品名}',
  receipt:'{編號} {產品名}{飯底名}便當',
  report:'{編號} {產品名}{飯底名}便當',
});

export const PRODUCT_VARIANT_TEMPLATE_MAX=80;

function record(value:unknown):Record<string,unknown>|null{
  return value&&typeof value==='object'&&!Array.isArray(value)?value as Record<string,unknown>:null;
}
function str(value:unknown,fallback=''){return typeof value==='string'?value:fallback;}

export function normalizeProductVariantSetConfig(value:unknown):ProductVariantSetConfigV1|undefined{
  const row=record(value);
  if(!row||row.enabled!==true)return undefined;
  const templates=record(row.templates)??{};
  const pick=(key:keyof ProductVariantTemplatesV1)=>{
    const raw=str(templates[key]).slice(0,PRODUCT_VARIANT_TEMPLATE_MAX);
    return raw.trim()?raw:DEFAULT_PRODUCT_VARIANT_TEMPLATES[key];
  };
  return Object.freeze({
    enabled:true,
    templates:Object.freeze({kitchen:pick('kitchen'),receipt:pick('receipt'),report:pick('report')}),
    defaultOptionId:str(row.defaultOptionId).trim(),
    showInDetail:row.showInDetail===true,
  });
}

/** 編號前綴：只准英文字母／數字／-，最多 6 個字（平台貨品編號要用到） */
export function normalizeVariantCodePrefix(value:unknown){
  return str(value).trim().toUpperCase().replace(/[^A-Z0-9-]/g,'').slice(0,6);
}

export interface ProductVariantTemplateFields{
  readonly prefix:string;
  readonly productCode:string;
  readonly productName:string;
  readonly shortName?:string;
  readonly optionName:string;
  readonly setName:string;
}

export function renderProductVariantTemplate(template:string,fields:ProductVariantTemplateFields){
  const values:Record<string,string>={
    '前綴':fields.prefix,
    '產品編號':fields.productCode,
    '編號':fields.prefix+fields.productCode,
    '產品名':fields.productName,
    '短名':fields.shortName?.trim()||fields.productName,
    '飯底名':fields.optionName,
    '選項名':fields.optionName,
    '組名':fields.setName,
  };
  return String(template||'')
    .replace(/\{([^{}]{1,8})\}/g,(whole,key:string)=>key in values?values[key]!:whole)
    .replace(/[ \u3000]{2,}/g,' ')
    .trim();
}

/** 落單時記低嘅變體結果（訂單入面唔再靠 Admin 設定重算，改咗範本都唔影響舊單） */
export interface MfkOrderLineVariantV1{
  readonly schema:typeof MFK_PRODUCT_VARIANT_SCHEMA;
  readonly setId:string;
  readonly setName:string;
  readonly optionId:string;
  readonly optionName:string;
  readonly codePrefix:string;
  readonly productCode:string;
  /** 前綴＋產品編號，例如 C12 */
  readonly code:string;
  readonly kitchenName:string;
  readonly receiptName:string;
  readonly reportName:string;
  /** 報表「按飯底」嘅大類名：用報表範本、前綴同選項名留空，例如「12 鹽酥雞便當」 */
  readonly reportGroup:string;
  readonly showInDetail:boolean;
}

export interface ProductVariantProductInput{
  readonly id:string;
  readonly name:string;
  readonly shortName?:string;
  readonly productCode?:string;
}
export interface ProductVariantOptionInput{
  readonly id:string;
  readonly name:string;
  readonly codePrefix?:string;
}
export interface ProductVariantSetInput{
  readonly id:string;
  readonly name:string;
  readonly variant?:ProductVariantSetConfigV1;
  readonly options:readonly ProductVariantOptionInput[];
}

export function resolveProductVariant(product:ProductVariantProductInput,set:ProductVariantSetInput,optionId:string):MfkOrderLineVariantV1|undefined{
  const config=set.variant;
  if(!config?.enabled)return undefined;
  const option=set.options.find(row=>row.id===optionId);
  if(!option)return undefined;
  const prefix=normalizeVariantCodePrefix(option.codePrefix);
  const productCode=String(product.productCode??'').trim();
  const fields:ProductVariantTemplateFields={prefix,productCode,productName:product.name,shortName:product.shortName,optionName:option.name,setName:set.name};
  return Object.freeze({
    schema:MFK_PRODUCT_VARIANT_SCHEMA,
    setId:set.id,
    setName:set.name,
    optionId:option.id,
    optionName:option.name,
    codePrefix:prefix,
    productCode,
    code:prefix+productCode,
    kitchenName:renderProductVariantTemplate(config.templates.kitchen,fields)||product.name,
    receiptName:renderProductVariantTemplate(config.templates.receipt,fields)||product.name,
    reportName:renderProductVariantTemplate(config.templates.report,fields)||product.name,
    reportGroup:renderProductVariantTemplate(config.templates.report,{...fields,prefix:'',optionName:''})||product.name,
    showInDetail:config.showInDetail,
  });
}

export function normalizeMfkOrderLineVariantV1(value:unknown):MfkOrderLineVariantV1|undefined{
  const row=record(value);
  if(!row||row.schema!==MFK_PRODUCT_VARIANT_SCHEMA)return undefined;
  const keys=['setId','setName','optionId','optionName','codePrefix','productCode','code','kitchenName','receiptName','reportName','reportGroup'] as const;
  if(keys.some(key=>typeof row[key]!=='string'))return undefined;
  if(!row.setId||!row.optionId||!row.receiptName)return undefined;
  const out:Record<string,unknown>={schema:MFK_PRODUCT_VARIANT_SCHEMA};
  for(const key of keys)out[key]=String(row[key]).slice(0,200);
  out.showInDetail=row.showInDetail===true;
  return Object.freeze(out) as unknown as MfkOrderLineVariantV1;
}

/** 平台：每個 B 渠道（Keeta／Foodpanda）可以揀「分開」（每個飯底一件貨，例如 C12）或者「合一」（一件貨＋選項）。預設分開（Owner 揀 B，暫定）。 */
export function channelVariantMode(channel:{readonly variantMode?:unknown;readonly provider?:unknown}|null|undefined):ChannelVariantMode{
  if(channel?.variantMode==='MERGED')return 'MERGED';
  if(channel?.variantMode==='SPLIT')return 'SPLIT';
  return 'SPLIT';
}

/** 拆開平台貨品編號：喺有變體組嘅產品入面，搵「前綴＋產品編號」完全相同嗰個。 */
export function matchVariantPlatformCode<P extends ProductVariantProductInput>(
  code:string,
  products:readonly {readonly product:P;readonly set:ProductVariantSetInput}[],
):{readonly product:P;readonly set:ProductVariantSetInput;readonly optionId:string}[]{
  const wanted=String(code||'').trim().toUpperCase();
  if(!wanted)return [];
  const hits:{product:P;set:ProductVariantSetInput;optionId:string}[]=[];
  for(const row of products){
    const productCode=String(row.product.productCode??'').trim().toUpperCase();
    if(!productCode)continue;
    for(const option of row.set.options){
      if(normalizeVariantCodePrefix(option.codePrefix)+productCode===wanted)hits.push({product:row.product,set:row.set,optionId:option.id});
    }
  }
  return hits;
}

/** Admin 驗證：同一組入面前綴唔可以重複（否則 C12 對唔返）；變體組一定係單選。 */
export function validateProductVariantSet(set:{readonly name:string;readonly selection:string;readonly variant?:unknown;readonly options:readonly {readonly id:string;readonly name:string;readonly codePrefix?:string;readonly active?:boolean}[]}){
  const config=normalizeProductVariantSetConfig(set.variant);
  if(!config)return [] as string[];
  const errors:string[]=[];
  const label=set.name||'變體選項組';
  if(set.selection!=='SINGLE')errors.push(label+'：編號變體組只可以係單選');
  const seen=new Map<string,string>();
  for(const option of set.options.filter(row=>row.active!==false)){
    const prefix=normalizeVariantCodePrefix(option.codePrefix);
    if(seen.has(prefix))errors.push(label+'：「'+seen.get(prefix)+'」同「'+option.name+'」編號前綴重複（'+(prefix||'空白')+'）');
    else seen.set(prefix,option.name);
  }
  if(config.defaultOptionId&&!set.options.some(row=>row.id===config.defaultOptionId&&row.active!==false))errors.push(label+'：預設選項已停用或者唔存在');
  for(const [key,value] of Object.entries(config.templates))if(!/\{(?:編號|產品名|短名|產品編號)\}/.test(value))errors.push(label+'：'+({kitchen:'製作單',receipt:'收據',report:'報表'} as Record<string,string>)[key]+'範本最少要有 {編號}／{產品名}／{短名}／{產品編號} 其中一個');
  return errors;
}
